import React, { useEffect, useMemo, useState } from "react";
import { Plus, ListChecks, Tags, Upload } from "lucide-react";
import DynamicModalForm from "../../Modal/DynamicModalForm";
import ListTable from "./ListTable";
import KanbanPageTask from "./KanbanPageTask";
import GanttComponent from "./GanttComponent";
import ManageLabelsModal from "../../Modal/ManageLabelsModal";
import TaskCardGrid from "./TaskCardGrid";
import { useTasksOptional } from "../../../_helper/Task/TaskProvider";
import { useLabels } from "../../../Hooks/useLabels";
import ImportButton from "../../Common/ImportButton";
import { useQuickAddRegistrar } from "../../../_helper/QuickAdd/QuickAddProvider";
import {
  TableToolbar,
  ViewSwitcher,
  IconButton,
  ListPageShell,
  useViewMode,
} from "../../../design-system";

export const defaultTaskFields = [
  { type: "text", label: "Title", name: "title", placeholder: "Title" },
  {
    type: "textarea",
    label: "Description",
    name: "description",
    placeholder: "Description",
  },
  {
    type: "select",
    label: "Related to",
    name: "relatedTo",
    options: [
      { value: "Project", label: "Project" },
      { value: "PMS", label: "PMS" },
      { value: "CRM", label: "CRM" },
    ],
  },
  { type: "text", label: "Project", name: "project", placeholder: "Project" },
  {
    type: "select",
    label: "Points",
    name: "points",
    options: [
      { value: 1, label: "1 Point" },
      { value: 2, label: "2 Points" },
      { value: 3, label: "3 Points" },
      { value: 4, label: "4 Points" },
      { value: 5, label: "5 Points" },
    ],
  },
  {
    type: "text",
    label: "Milestone",
    name: "milestone",
    placeholder: "Milestone",
  },
  {
    type: "text",
    label: "Assign to",
    name: "assignedTo",
    placeholder: "Assign to",
  },
  {
    type: "text",
    label: "Collaborators",
    name: "collaborators",
    placeholder: "Collaborators",
  },
  {
    type: "select",
    label: "Status",
    name: "status",
    options: [
      { value: "To Do", label: "To Do" },
      { value: "In Progress", label: "In Progress" },
      { value: "Completed", label: "Completed" },
    ],
  },
  {
    type: "select",
    label: "Priority",
    name: "priority",
    options: [
      { value: "low", label: "Low" },
      { value: "middle", label: "Middle" },
      { value: "high", label: "High" },
    ],
  },
  {
    type: "multiselect",
    label: "Labels",
    name: "labels",
    options: [
      { value: "Yazılım", label: "Yazılım" },
      { value: "Analiz", label: "Analiz" },
      { value: "PPC", label: "PPC" },
      { value: "Design", label: "Design" },
      { value: "Bug", label: "Bug" },
    ],
  },
  {
    type: "dateTimeRow",
    label: "Start",
    leftDateName: "date",
    rightTimeName: "time",
  },
  {
    type: "dateTimeRow",
    label: "Deadline",
    leftDateName: "deadlineDate",
    rightTimeName: "deadlineTime",
  },
  { type: "checkbox", label: "Recurring", name: "recurring" },
];

export const initialForm = {
  title: "",
  description: "",
  relatedTo: "Project",
  project: "",
  points: null,
  milestone: "",
  assignedTo: "",
  collaborators: "",
  status: "To Do",
  priority: null,
  labels: [],
  startDate: "",
  startTime: "",
  deadline: "",
  endTime: "",
  recurring: false,
};

const getNextNumericTaskId = (taskList = []) => {
  const maxId = (Array.isArray(taskList) ? taskList : []).reduce((max, task) => {
    const rawId = String(task?.id ?? "").trim();
    if (!/^\d+$/.test(rawId)) return max;
    const numericId = Number(rawId);
    return Number.isFinite(numericId) && numericId > max ? numericId : max;
  }, 0);

  return String(maxId + 1);
};

const ListTask = ({
  title = "Tasks",
  views = ["list", "kanban", "gantt"],
  defaultView,
  actions = {},
  taskFields = defaultTaskFields,
  tasks: controlledTasks,
  setTasks: setControlledTasks,
  onTaskAdd,
  onTaskEdit,
  onTaskDelete,
  onOpenInfo,
  showInfoAfterAdd = true,
  listTableProps = {},
  inspectorApi,
  activeTaskId = null,
  useInspector = false,
  labels: labelsProp,
  setLabels: setLabelsProp,
  labelOptions: labelOptionsProp,
  labelColorMap: labelColorMapProp,
  listViewComponent: ListViewComponent,
}) => {
  const tasksCtx = useTasksOptional();
  const [localTasks, setLocalTasks] = useState([]);
  const tasks = controlledTasks ?? tasksCtx?.tasks ?? localTasks;
  const setTasks = setControlledTasks ?? tasksCtx?.setTasks ?? setLocalTasks;

  const labelValues = useMemo(() => {
    return (tasks || []).flatMap((t) => {
      if (Array.isArray(t.labels)) return t.labels;
      if (t.labels) return [t.labels];
      return [];
    });
  }, [tasks]);
  const labelState = useLabels(labelValues);
  const labels = labelsProp ?? labelState.labels;
  const setLabels = setLabelsProp ?? labelState.setLabels;
  const labelOptions = labelOptionsProp ?? labelState.labelOptions;
  const labelColorMap = labelColorMapProp ?? labelState.labelColorMap;

  const availableViews = useMemo(
    () => (views && views.length ? views : ["list"]),
    [views]
  );
  const initialView = useMemo(() => {
    const fallback = availableViews[0] || "list";
    if (defaultView && availableViews.includes(defaultView)) return defaultView;
    return fallback;
  }, [defaultView, availableViews]);
  const [view, setView] = useState(initialView);
  const [viewMode, setViewMode] = useViewMode("tasks", "list-1");

  useEffect(() => {
    if (!availableViews.includes(view)) {
      setView(availableViews[0] || "list");
    }
  }, [availableViews, view]);

  const mergedActions = {
    showAdd: true,
    showAddMultiple: true,
    showImport: true,
    showManageLabels: true,
    ...actions,
  };

  const openCreatePanel = () =>
    inspectorApi?.openInspector({ mode: "create" });
  const openMultipleCreatePanel = () =>
    inspectorApi?.openInspector({
      mode: "create",
      payload: { variant: "multiple" },
    });
  const openManageLabelsPanel = () =>
    inspectorApi?.openInspector({
      mode: "manage-labels",
    });
  const openImportPanel = () =>
    inspectorApi?.openInspector({
      mode: "import",
      payload: {
        title: "Import tasks",
        fields: resolvedTaskFields,
        setRows: setTasks,
      },
    });

  useQuickAddRegistrar(
    {
      addTask: mergedActions.showAdd
        ? () => {
            if (useInspector) openCreatePanel();
            else setShowSingleModal(true);
          }
        : undefined,
      addMultipleTasks: mergedActions.showAddMultiple
        ? () => {
            if (useInspector) openMultipleCreatePanel();
            else setShowMultipleModal(true);
          }
        : undefined,
    },
    [mergedActions.showAdd, mergedActions.showAddMultiple, useInspector, inspectorApi]
  );

  const [showSingleModal, setShowSingleModal] = useState(false);
  const [showMultipleModal, setShowMultipleModal] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(false);

  const [editFormData, setEditFormData] = useState(initialForm);
  const [singleFormData, setSingleFormData] = useState(initialForm);
  const [multipleFormData, setMultipleFormData] = useState(initialForm);

  const handleAddTask = (newTask, options = { showInfo: showInfoAfterAdd }) => {
    const newId = getNextNumericTaskId(tasks);
    const taskWithId = { ...newTask, id: newId };
    setTasks((prev) => {
      const nextList = [...prev, taskWithId];
      return nextList;
    });
    onTaskAdd?.(taskWithId);

    if (options.showInfo && showInfoAfterAdd) {
      // Info modal yönetimi ListTable üzerinden yapılacak.
    }
    setSingleFormData(initialForm);
    setMultipleFormData(initialForm);
  };

  const handleEditTask = (updatedTask) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
    );
    onTaskEdit?.(updatedTask);
  };

  const handleDeleteTask = (taskOrId) => {
    const id = typeof taskOrId === "object" ? taskOrId?.id : taskOrId;
    if (!id) return;
    setTasks((prev) => prev.filter((t) => t.id !== id));
    onTaskDelete?.(id);
  };

  const openEditModal = (task) => {
    if (useInspector) {
      inspectorApi?.openInspector({ mode: "edit", entityId: task?.id });
      return;
    }
    setEditFormData(task);
    setShowSingleModal(true);
  };

  const resolvedTaskFields = useMemo(
    () =>
      (taskFields || []).map((field) => {
        if (field?.name === "labels") {
          return {
            ...field,
            type: "multiselect",
            options: labelOptions,
          };
        }
        return field;
      }),
    [taskFields, labelOptions]
  );

  const VIEW_LABELS = { list: "List", kanban: "Kanban", gantt: "Gantt" };
  const toolbarTabs =
    availableViews.length > 1
      ? availableViews.map((v) => ({
          value: v,
          label: VIEW_LABELS[v] || v,
        }))
      : null;

  const handleManageLabels = () => {
    if (useInspector) openManageLabelsPanel();
    else setShowLabelsModal(true);
  };
  const handleAddMultiple = () => {
    if (useInspector) openMultipleCreatePanel();
    else setShowMultipleModal(true);
  };
  const handleAdd = () => {
    if (useInspector) openCreatePanel();
    else setShowSingleModal(true);
  };

  const toolbarActions = (
    <>
      {mergedActions.showManageLabels && (
        <IconButton
          variant="outlined"
          size="md"
          ariaLabel="Manage labels"
          onClick={handleManageLabels}
        >
          <Tags size={16} />
        </IconButton>
      )}
      {mergedActions.showImport && useInspector && (
        <IconButton
          variant="outlined"
          size="md"
          ariaLabel="Import tasks"
          onClick={openImportPanel}
        >
          <Upload size={16} />
        </IconButton>
      )}
      {mergedActions.showImport && !useInspector && (
        <ImportButton
          title="Import tasks"
          label="Import tasks"
          fields={resolvedTaskFields}
          setRows={setTasks}
          variant="iconOnly"
          ariaLabel="Import tasks"
        />
      )}
      {mergedActions.showAddMultiple && (
        <IconButton
          variant="outlined"
          size="md"
          ariaLabel="Add multiple tasks"
          onClick={handleAddMultiple}
        >
          <ListChecks size={16} />
        </IconButton>
      )}
      {mergedActions.showAdd && (
        <IconButton
          variant="solid"
          size="md"
          ariaLabel="Add task"
          onClick={handleAdd}
        >
          <Plus size={18} />
        </IconButton>
      )}
    </>
  );

  const ResolvedListView = ListViewComponent || ListTable;
  const showCardGrid = view === "list" && viewMode !== "list-1";

  return (
    <ListPageShell>
      <TableToolbar
        title={title}
        tabs={toolbarTabs}
        activeTab={view}
        onTabChange={setView}
        viewSlot={
          view === "list" ? (
            <ViewSwitcher value={viewMode} onChange={setViewMode} />
          ) : null
        }
        actions={toolbarActions}
      />

      <ListPageShell.Content
        data-mode={view === "list" && !showCardGrid ? "list" : "card"}
      >
        {view === "list" && !showCardGrid && (
          <ResolvedListView
            rows={tasks}
            setRows={setTasks}
            onEdit={openEditModal}
            onDelete={handleDeleteTask}
            onOpenInfo={onOpenInfo}
            labelColors={labelColorMap}
            activeTaskId={activeTaskId}
            inspectorApi={inspectorApi}
            toolbarActions={null}
            {...listTableProps}
          />
        )}
        {showCardGrid && (
          <TaskCardGrid
            tasks={tasks}
            viewMode={viewMode}
            onOpen={onOpenInfo || openEditModal}
            onEdit={openEditModal}
          />
        )}
        {view === "kanban" && availableViews.includes("kanban") && (
          <KanbanPageTask tasks={tasks} setTasks={setTasks} />
        )}
        {view === "gantt" && availableViews.includes("gantt") && (
          <GanttComponent tasks={tasks} />
        )}
      </ListPageShell.Content>

      {/* Modals */}
      {!useInspector && mergedActions.showAdd && (
        <DynamicModalForm
          isOpen={showSingleModal}
          toggle={() => setShowSingleModal(false)}
          title={editFormData?.id ? "Edit Task" : "Add Task"}
          formData={editFormData?.id ? editFormData : singleFormData}
          setFormData={editFormData?.id ? setEditFormData : setSingleFormData}
          onSave={(data) => {

            if (editFormData?.id) handleEditTask(data);
            else handleAddTask(data);
            setShowSingleModal(false);
            setEditFormData(initialForm);
          }}
          fields={resolvedTaskFields}
          mode="single"
        />
      )}

      {!useInspector && mergedActions.showAddMultiple && (
        <DynamicModalForm
          isOpen={showMultipleModal}
          toggle={() => setShowMultipleModal(false)}
          title="Add Multiple Tasks"
          formData={multipleFormData}
          setFormData={setMultipleFormData}
          onSave={handleAddTask}
          fields={resolvedTaskFields}
          mode="multiple"
        />
      )}

      {!useInspector && mergedActions.showManageLabels && (
        <ManageLabelsModal
          isOpen={showLabelsModal}
          toggle={() => setShowLabelsModal(false)}
          labels={labels}
          setLabels={setLabels}
        />
      )}
    </ListPageShell>
  );
};

export default ListTask;
